<template>
  <div :key="epgIdentifier"
       class="bx-content bx-search bx-typo--search bx-typo--epg bx-typo--vod-epg bx-epg-search"
       :class="{
         [`bx-epg-search--${viewMode}`]: true,
         'bx-epg-search--hide-filters': config.hideFilters
       }">
    <FilterContainer :initial-filters="sessionStorage.selectedFilters || initialFilters"
                     :view-mode="viewMode"
                     :config="config"
                     @update-filters="updateFilters($event)"
                     @filters-ready="onFiltersReady($event)" />
    <section class="bx-epg-search__broadcasts"
             :class="{ 'bx-js-loading': progressSearch }">
      <div v-if="progressSearch"
           class="bx-form__loader" />
      <BroadcastList v-if="broadcasts.length"
                     :epg-data="broadcasts"
                     :view-mode="viewMode" />
      <div v-else-if="!progressSearch"
           class="bx-search__no-results">
        <span>
          <strong>Keine passenden Sendungen gefunden.</strong><br><br>
          Vielleicht läuft dein Wunschprogramm zu einer anderen Zeit oder auf einem anderen Sender?<br><br>
          Passe einfach die Filter an und entdecke mehr Inhalte!
        </span>
      </div>

      <div v-if="moreToLoad || config.loadMoreUrl"
           class="bx-form__item bx-form__item--centered bx-typo--form">
        <a v-if="config.loadMoreUrl"
           :href="config.loadMoreUrl"
           class="bx-form__button">
          Mehr Laden
        </a>
        <button v-else-if="moreToLoad"
                class="bx-form__button"
                type="button"
                @click.prevent="fetchBroadcasts(true)">
          Mehr Laden
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'pinia'

import adaMoltenBundle from '../../../../mixins/ada-molten-bundle'
import epgAirTimeHelper from '../mixins/epg-air-time-helper'
import EventBus from '../../../../event-bus/EventBus'
import { useConfigStore } from '../../../../stores/config'
import { usePageStore } from '../../../../stores/page'
import BroadcastList from '../EpgSearch/BroadcastList.vue'
import FilterContainer from '../EpgSearch/Filter/Container.vue'
import { hashString } from '../../../../utils/strings.ts'

export default {
  name: 'ParagraphTypeEpgSearch',
  components: {
    BroadcastList,
    FilterContainer
  },
  mixins: [epgAirTimeHelper, adaMoltenBundle],
  props: {
    /**
     * Possible prop for vueComponent, maybe used to pass filter settings for integration in different contexts
     * i.e. [{"key":"filter","value":"blafasel-json-string-with-filter-setting"},{"key":"filterB","value":"or-separat-parameter-for-each-filter-option"}]
     */
    // eslint-disable-next-line vue/no-unused-properties
    params: {
      type: Array,
      default: null
    },
    config: {
      type: Object,
      default: () => ({})
    },
    initialFilters: {
      type: Object,
      default: () => ({})
    },
    initialBroadcasts: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      epgIdentifier: null,
      progressSearch: false,
      selectedFilters: {},
      broadcasts: this.initialBroadcasts || [],
      broadcastsLoadedTotal: 0,
      broadcastsResultSetsTotal: 0,
      isFiltersInitialized: false, // flag for initial search
      searchMeta: {
        total: null,
        offset: null,
        limit: null
      },
      allowSessionStorage: false,
      sessionStorageDuration: 5 * 60 * 1000, // 5 minutes
      sessionStorage: {
        // TODO: will be used in the next step, when we validate the session storage data explicitly i.e. for broadcasts now
        // eslint-disable-next-line vue/no-unused-properties
        updatedAt: null,
        selectedFilters: null,
        broadcasts: null,
        searchMeta: null
      },
      sessionStorageItemName: {
        searchData: 'epgSearchData',
        scrollPosition: 'epgScrollPosition'
      }
    }
  },
  computed: {
    ...mapState(useConfigStore, ['rsConfig']), // we need the epg search service url
    ...mapState(usePageStore, ['isPreview', 'brandFromStore', 'pageData']), // in case it's an epg channel detail page (e.g. /tv/sender-ard), we'll need the (node) id from the page meta and pass it as channel id param to the epg search service
    /**
     * Returns the params as object
     * @returns {Object}
     */
    // eslint-disable-next-line vue/no-unused-properties
    paramsMap () {
      return Object.fromEntries(
        (this.params || []).map(item => [item.key, item.value])
      )
    },
    channelId () {
      return this.pageData?.pageMeta?.nodeId
    },
    isChannelDetailPage () {
      return this.channelId && (this.isPreview || this.pageData?.pageMeta?.url?.includes('/tv/sender'))
    },
    viewMode () {
      // if the page is a channel detail page, we will set the view mode to "channel"
      return this.isChannelDetailPage ? 'channel' : 'overview'
    },
    moreToLoad () {
      return this.isMultiChannelSplitRequest ? this.searchMeta.total > this.broadcastsResultSetsTotal : this.searchMeta.total > this.broadcastsLoadedTotal
    },
    reloadAdsOnInteraction () {
      return this.rsConfig.adSlotUsage.reloadAdsOnInteraction
    },
    isMultiChannelSplitRequest () {
      return !this.channelId && !this.selectedFilters.isDailyTip && this.selectedFilters.categoryIds?.length === 0
    }
  },
  watch: {
    // watch for changes in the selected filters and fetch broadcasts accordingly
    selectedFilters: {
      deep: true,
      handler () {
        if (this.isFiltersInitialized) {
          this.fetchBroadcasts()
        }
      }
    },
    // watch for changes in the broadcasts array and update the loaded total
    broadcasts: {
      handler (newVal) {
        this.broadcastsLoadedTotal = newVal.reduce((sum, resultSet) => sum + resultSet.broadcasts.length, 0)
        this.broadcastsResultSetsTotal = newVal.length
      },
      immediate: true,
      deep: true
    }
  },
  beforeMount () {
    this.initSessionStorage()
    this.restoreEpgPageData()
  },
  mounted () {
    // before the user leaves the page, we will save the scroll position in session storage
    // to restore it when the user comes back
    window.addEventListener('beforeunload', this.storeLastScrollY)
    window.addEventListener('resize', this.setActualVh)
    window.addEventListener('orientationchange', this.setActualVh)
    this.setActualVh()
  },
  beforeUnmount () {
    // remove the event listeners to avoid memory leaks
    window.removeEventListener('beforeunload', this.storeLastScrollY)
    window.removeEventListener('resize', this.setActualVh)
    window.removeEventListener('orientationchange', this.setActualVh)
  },
  methods: {
    storeLastScrollY () {
      // store the last scroll position in session storage
      sessionStorage.setItem(this.sessionStorageItemName.scrollPosition, window.scrollY)
    },
    setActualVh () {
      document.documentElement.style.setProperty('--epg-filter-vh', `${window.innerHeight}px`)
    },
    /*
     * Update the selected filters with new values.
     * Merges the new filters with the existing ones.
     * @param {Object} newFilters - The new filters to be merged.
     */
    updateFilters (newFilters) {
      this.selectedFilters = { ...this.selectedFilters, ...newFilters }

      if (this.isFiltersInitialized) {
        this.triggerAdReload()
      }

      if (this.allowSessionStorage) {
        window.bxEpgDebug[this.epgIdentifier] = {
          initialFilters: JSON.parse(JSON.stringify(this.initialFilters)),
          selectedFilters: JSON.parse(JSON.stringify(this.selectedFilters))
        }
      }
    },

    /**
     * Handle the event when filters are ready.
     * This method updates the selected filters and fetches broadcasts if there are none delivered yet.
     * @param {Object} newFilters - The new filters to be set.
     */
    onFiltersReady (newFilters) {
      this.updateFilters(newFilters)

      // set the default values for offset and limit
      this.searchMeta.offset = this.selectedFilters.offset
      this.searchMeta.limit = this.selectedFilters.limit
      this.searchMeta.total = this.selectedFilters.total

      if (this.sessionStorage.searchMeta) {
        // if there are search values in session storage, we will set them to the search object
        this.searchMeta = { ...this.searchMeta, ...this.sessionStorage.searchMeta }
      }

      this.$nextTick(() => {
        this.isFiltersInitialized = true
      })

      let validateBroadcasts = true

      if (this.sessionStorage.broadcasts?.length > 0) {
        // if there are broadcasts in session storage, we will set them to the broadcasts array
        this.broadcasts = this.sessionStorage.broadcasts
      } else if (this.initialBroadcasts.length === 0) {
        // if there are no broadcasts delivered yet, start the first search
        this.fetchBroadcasts() // start the first search once the filters are ready and there are no broadcasts delivered
        validateBroadcasts = false // we don't need to validate the broadcasts here, because we are loading them for the first time
      }

      if (validateBroadcasts) {
        // if broadcasts are delivered initially or in session storage, we will validate them
        this.triggerBroadcastsValidation()
      }
    },

    /*
     * Fetch broadcasts from the EPG search service.
     * If "loadMore" is true, it will load more broadcasts based on the current offset.
     * Otherwise, it will reset the offset and fetch fresh results.
     * @param {boolean} loadMore - Whether to load more broadcasts or not.
     */
    async fetchBroadcasts (loadMore = false) {
      if (this.progressSearch || import.meta.env.SSR) {
        return
      }

      this.progressSearch = true
      const url = this.rsConfig.epgSearchServiceUrl // 'http://localhost:3200/v2/epg-search'
      const params = {}

      // If "loadMore" is true, increase offset; otherwise, reset to 0 for a new search
      this.searchMeta.offset = loadMore ? this.searchMeta.offset + this.searchMeta.limit : 0

      const { airTimeOptions, airTime, packageName, categoryIds, isDailyTip } = this.selectedFilters

      // we only need to set the airTime if actively selected by the user via the filter form (e.g. 20:15, 22:00, custom, not now)
      if (airTimeOptions !== 'now') {
        params.airTime = airTime
      }

      // channelId are extracted on a channel detail page (e.g. /tv/sender-ard) in this case we will not set a package name
      if (this.isChannelDetailPage) {
        params.channelId = this.channelId
        // and if there is no channelId, we will only set the package name if it is not "all"
      } else if (packageName !== 'all') {
        params.packageName = packageName
      }

      // append categoryIds if there are any selected
      if (categoryIds.length > 0) {
        params.categoryIds = categoryIds.join(',')
      }

      // and if daily tips are selected, we will append it to the params also
      if (isDailyTip) {
        params.isDailyTip = isDailyTip
      }

      params.offset = this.searchMeta.offset
      params.limit = this.searchMeta.limit

      try {
        const { data } = await axios.get(url, { params })
        this.searchMeta.total = data.total

        // If "loadMore" is true, append new results to the existing list
        // Otherwise, replace the broadcast list with fresh results
        if (this.viewMode === 'overview') {
          this.broadcasts = loadMore ? [...this.broadcasts, ...data.results] : data.results || []
        } else {
          // If the view mode is "channel", we need to handle the broadcasts differently
          // We need to check if the first broadcast is already loaded
          if (loadMore) {
            // If "loadMore" is true, append new broadcasts to the existing list
            this.broadcasts[0].broadcasts = [...this.broadcasts[0].broadcasts, ...data.results[0].broadcasts]
          } else {
            // If "loadMore" is false, replace the broadcasts with fresh results
            this.broadcasts = data.results || []
          }
        }
      } catch (error) {
        console.error('Error fetching broadcasts:', error)
      }

      this.saveEpgPageData()
      this.progressSearch = false

      if (loadMore) {
        this.triggerAdReload()
      }
    },
    triggerAdReload () {
      if (this.reloadAdsOnInteraction.enabled && this.allowSessionStorage) {
        // Trigger GA, IVW and AGF tracking on ad reload
        EventBus.$emit('triggerIVW')
        EventBus.$emit('triggerAGF')
        // Google Analytics tracking
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'epg_filter_result_page_view',
          timeDate: this.selectedFilters.airTimeOptions,
          programType: this.selectedFilters.categoryIds.map(catId => Object.assign({}, ...this.rsConfig.epg.broadcastCategories)[parseInt(catId)]).join(', ') || 'none',
          channel: this.selectedFilters.packageName,
          tipOfTheDay: this.selectedFilters.isDailyTip ? 'on' : 'off'
        })

        // Schedule ad reload OUT of the input event to avoid blocking the input event
        setTimeout(() => {
          this.adaSlotReloadOnInteraction({ ...this.reloadAdsOnInteraction.config, clickCount: 1 })
        }, 0)
      }
    },
    initSessionStorage () {
      // define Identifier for this Instance to prevent problems with multiple instances on the same page
      const epgOrderPosition = this.$el?.closest('[data-lop]')?.getAttribute('data-lop') || 'Unknown'
      this.epgIdentifier = `epgPos${epgOrderPosition}`
      // check if we have a valid identifier
      if (this.epgIdentifier !== 'epgPosUnknown') {
        // allow session storage and create array in global scope to get known how many instances are on the page
        this.allowSessionStorage = true

        // make the session storage unique for each url, so that the user can navigate between different epg pages without losing the data
        const hrefHash = hashString(window.location.href)
        this.sessionStorageItemName.searchData += '_' + hrefHash
        this.sessionStorageItemName.scrollPosition += '_' + hrefHash

        window.bxEpgInstances = window.bxEpgInstances || []
        window.bxEpgInstances.push(this.epgIdentifier)
        window.bxEpgDebug = window.bxEpgDebug || {}
      }
    },

    /*
     * Save the current epgSearchData for this instance in session storage.
     * This is used to restore the data when the user comes back to the page.
     * The data is saved in the session storage with a unique identifier for this instance.
     * This includes selectedFilters, broadcasts, searchMeta values, the last URL as scope
     * and time of last page view.
     */
    saveEpgPageData () {
      // check if we have a valid identifier
      if (!this.allowSessionStorage) return
      const epgSearchData = JSON.parse(sessionStorage.getItem(this.sessionStorageItemName.searchData) || '{}')
      epgSearchData.scope = window.location.href
      epgSearchData.lastPageViewAt = new Date().getTime()
      epgSearchData.epgInstances = window.bxEpgInstances
      epgSearchData[this.epgIdentifier] = {
        updatedAt: new Date().getTime(),
        selectedFilters: this.selectedFilters,
        broadcasts: this.broadcasts,
        searchMeta: this.searchMeta
      }
      sessionStorage.setItem(this.sessionStorageItemName.searchData, JSON.stringify(epgSearchData))
    },

    /*
     * Restore the page data from session storage for this instance.
     * This includes selectedFilters, broadcasts, searchMeta values, and scroll position.
     * but only if the last page view is not older than the sessionStorageDuration.
     * If the last page view is older than the sessionStorageDuration, the data will be removed from session storage.
     * If the scope is not the same as the current one, the data will also be removed from session storage.
     */
    restoreEpgPageData () {
      // check if we have a valid identifier
      if (!this.allowSessionStorage) return

      // load the epg page data from session storage
      const epgSearchData = JSON.parse(sessionStorage.getItem(this.sessionStorageItemName.searchData) || '{}')

      const isDurationValid = (epgSearchData.lastPageViewAt + this.sessionStorageDuration) > new Date().getTime()
      // check if we are in the same scope as the last saved data and if the duration is valid
      if (epgSearchData.scope !== window.location.href || !isDurationValid) {
        // if the last page view is older than the sessionStorageDuration, remove the old data
        // or if the scope is not the same as the current one, remove the old data
        sessionStorage.removeItem(this.sessionStorageItemName.searchData)
        sessionStorage.removeItem(this.sessionStorageItemName.scrollPosition)
        return
      }

      // check if we have stored data for this instance
      if (epgSearchData[this.epgIdentifier]) {
        // load the data from session storage to the instance sessionStorage
        this.sessionStorage = epgSearchData[this.epgIdentifier]
      }

      // check if we saved the last scroll position
      const lastScrollPosition = sessionStorage.getItem(this.sessionStorageItemName.scrollPosition)
      if (lastScrollPosition) {
        // if there is a last scroll position, we will scroll to it
        window.scrollTo(0, parseInt(lastScrollPosition, 0))
      }

      // update the last page view time
      epgSearchData.lastPageViewAt = new Date().getTime()
      // save the updated data to session storage
      sessionStorage.setItem(this.sessionStorageItemName.searchData, JSON.stringify(epgSearchData))
    },

    /**
     * Trigger the validation of broadcasts.
     * This method is called for initial and session storage data.
     */
    triggerBroadcastsValidation () {
      // If the view mode is "channel", we don't need to validate the broadcasts
      if (this.viewMode === 'channel') return

      // Trigger the validation of broadcasts
      let airTimeOption = this.selectedFilters.airTimeOptions
      let toleranceMs = 2.5 * 60 * 1000 // 2.5 minutes

      if (this.selectedFilters.isDailyTip) {
        // If daily tips are selected, set airTime to today at 12:00
        airTimeOption = 'today,12:00'
        toleranceMs = 12 * 60 * 60 * 1000 // 12 hours
      }

      const validationResult = this.validateBroadcasts(this.broadcasts, airTimeOption, toleranceMs)

      // TODO: remove this log after testing
      console.log('### Validation results for ' + this.epgIdentifier, validationResult)

      if (validationResult.percentValid < 70) {
        // If there are less than 70 percent valid items, we fetch actual broadcasts from the server
        this.fetchBroadcasts()
      }
    },

    /**
     * Prüft, ob der erste Broadcast je Channel zum gegebenen Zeitpunkt passt
     * @param {Array} epgData - Liste der Channels inkl. Broadcasts
     * @param {Date | string} checkTime - Der Zeitpunkt, der geprüft werden soll (z.B. neue Date() oder ISO-String)
     * @param {number} toleranceMs - Erlaubte Abweichung in Millisekunden (Standard: 0)
     * @returns {{ total: number, valid: number, invalid: number, percentValid: number, invalidItems: Array }}
     */
    validateBroadcasts (epgData, airTimeOptions, toleranceMs = 0) {
      const airTimeByOption = this.getAirTimeByAirTimeOption(airTimeOptions)
      const timeToCheck = new Date(airTimeByOption).getTime()

      // TODO: remove this log after testing
      console.log('### validateBroadcasts for ', this.epgIdentifier, airTimeOptions, airTimeByOption, timeToCheck, toleranceMs)

      let valid = 0
      let invalid = 0

      for (const item of epgData) {
        const broadcast = item.broadcasts?.[0]
        if (!broadcast) {
          invalid++
          continue
        }

        const start = new Date(broadcast.airTime).getTime()
        const end = new Date(broadcast.airTimeEnd).getTime()

        const inRange =
          timeToCheck >= start - toleranceMs &&
          timeToCheck <= end + toleranceMs

        if (inRange) {
          valid++
        } else {
          invalid++
        }
      }

      const total = valid + invalid
      const percentValid = total > 0 ? (valid / total) * 100 : 0

      return {
        total,
        valid,
        invalid,
        percentValid: Math.round(percentValid * 100) / 100
      }
    }
  }
}
</script>
